// ReceivedGiftCardsPage.jsx
import { useState, useEffect } from 'react';
import { summaryApi } from 'common';
import { Calendar, CheckCircle, DollarSign, Gift } from 'lucide-react';
import { useTheme } from "../../components/Theme/ThemeProvider";
import { authFetch } from '../../helpers/authFetch';

export function ReceivedGiftCardsPage() {
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { colors } = useTheme();

  useEffect(() => {
    fetchReceivedGiftCards();
  }, []);

  const fetchReceivedGiftCards = async () => {
    try {
      const response = await authFetch(summaryApi.getReceivedGiftCards.url, {
        method: 'GET',
      
      headers: {
        'Content-Type': 'application/json'
      }
      });
      const data = await response.json();

      if (data.success) {
        setGiftCards(data.data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to load gift cards');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: `${colors.success}20`, text: colors.success };
      case 'redeemed': return { bg: `${colors.textSecondary}20`, text: colors.textSecondary };
      case 'expired': return { bg: `${colors.error}20`, text: colors.error };
      default: return { bg: `${colors.textSecondary}20`, text: colors.textSecondary };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.bgPrimary }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
            style={{ borderColor: colors.primary, borderTopColor: 'transparent' }} />
          <p style={{ color: colors.textSecondary }}>Loading your gift cards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: colors.bgPrimary }}>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: colors.text }}>Received Gift Cards</h1>
          <p style={{ color: colors.textSecondary }}>Gift cards sent to you</p>
        </div>

        {error && (
          <div className="border rounded-lg p-4 mb-6" style={{
            backgroundColor: `${colors.error}10`,
            borderColor: colors.error
          }}>
            <p style={{ color: colors.error }}>{error}</p>
          </div>
        )}

        {giftCards.length === 0 ? (
          <div className="rounded-lg shadow-lg p-12 text-center" style={{ backgroundColor: colors.bgSecondary }}>
            <Gift className="w-16 h-16 mx-auto mb-4" style={{ color: colors.textSecondary }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: colors.text }}>No Gift Cards Received</h3>
            <p style={{ color: colors.textSecondary }}>You haven't received any gift cards yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {giftCards.map((card) => (
              <div key={card._id} className="rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition" 
                style={{ backgroundColor: colors.bgSecondary }}>
                <div className="p-6 text-white" style={{
                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.primary} 100%)`
                }}>
                  <div className="flex justify-between items-start mb-4">
                    <Gift className="w-8 h-8" />
                    <span className="px-3 py-1 rounded-full text-xs font-medium" style={{
                      backgroundColor: getStatusColor(card.status).bg,
                      color: getStatusColor(card.status).text
                    }}>
                      {card.status}
                    </span>
                  </div>
                  <p className="text-3xl font-bold mb-2">₦{card.currentBalance.toLocaleString()}</p>
                  <p className="text-sm opacity-75">Available Balance</p>
                </div>

                <div className="p-6 space-y-3">
                  <div className="rounded-lg p-3" style={{ backgroundColor: colors.bgTertiary }}>
                    <p className="text-xs mb-1" style={{ color: colors.textSecondary }}>Gift Card Code</p>
                    <p className="font-mono font-bold" style={{ color: colors.primary }}>{card.code}</p>
                  </div>

                  {card.message && (
                    <div className="rounded-lg p-3" style={{ backgroundColor: `${colors.primary}10` }}>
                      <p className="text-sm italic" style={{ color: colors.text }}>"{card.message}"</p>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="w-4 h-4" style={{ color: colors.textSecondary }} />
                    <span style={{ color: colors.textSecondary }}>Original:</span>
                    <span className="font-medium" style={{ color: colors.text }}>
                      ₦{card.originalAmount.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4" style={{ color: colors.textSecondary }} />
                    <span style={{ color: colors.textSecondary }}>Expires:</span>
                    <span className="font-medium" style={{ color: colors.text }}>
                      {new Date(card.expiryDate).toLocaleDateString()}
                    </span>
                  </div>

                  {card.status === 'active' && card.currentBalance > 0 && (
                    <button
                      onClick={() => window.location.href = '/shop'}
                      className="w-full mt-4 px-4 py-3 rounded-lg text-white transition flex items-center justify-center gap-2"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <CheckCircle className="w-5 h-5" />
                      Use Gift Card
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
